import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VinScanner from "@/components/VinScanner";
import { toast } from "sonner";
import {
  ScanLine, Car, User, ClipboardList, Wrench, DollarSign, PaintBucket, ShieldCheck,
  ChevronRight, AlertTriangle, CheckCircle2, RefreshCw, PlusCircle, FileDown
} from "lucide-react";

const JOB_TYPE_META = {
  mechanic: { label: "Mecânica", icon: Wrench, cls: "text-primary" },
  body_shop: { label: "Body Shop", icon: PaintBucket, cls: "text-pink-500" },
  warranty: { label: "Garantia Estendida", icon: ShieldCheck, cls: "text-purple-500" },
};

const STATUS_LABELS = {
  entrou: "Veículo recebido",
  em_inspecao: "Em Inspeção",
  aguardando_estimate: "Aguardando Estimate",
  aguardando_aprovacao: "Aguardando Aprovação",
  aguardando_pecas: "Aguardando Peças",
  em_andamento: "Serviço em Andamento",
  pronto: "Serviço Pronto",
  entregue: "Entregue",
  novo: "Novo",
  aprovado: "Aprovado",
  em_reparo: "Em Reparo",
  pintura: "Pintura",
  montagem: "Montagem",
  quality_check: "Quality Check",
  pronto_entrega: "Pronto para Entrega",
  agendado: "Agendado",
  recebido: "Recebido",
  estimate: "Estimate",
  kit_enviado: "Kit Enviado",
  aguardando_pagamento: "Aguardando Pagamento",
};

const STATUSES_BY_TYPE = {
  mechanic: ["entrou","em_inspecao","aguardando_estimate","aguardando_aprovacao","aguardando_pecas","em_andamento","pronto","entregue"],
  body_shop: ["novo","em_inspecao","aguardando_aprovacao","aprovado","aguardando_pecas","em_reparo","pintura","montagem","quality_check","pronto_entrega","entregue"],
  warranty: ["entrou","agendado","recebido","em_inspecao","estimate","kit_enviado","aguardando_aprovacao","aprovado","em_reparo","pronto","entregue","aguardando_pagamento"],
};

export default function CheckStatus() {
  const navigate = useNavigate();
  const [vin, setVin] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(null); // job id being updated

  const lookup = async (v) => {
    if (!v || v.length !== 17) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vehicles/by-vin/${v}`);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao buscar");
      setResult(null);
    } finally { setLoading(false); }
  };

  const onDecoded = () => {
    // vin already updated via onChange, do the lookup
    lookup(vin);
  };

  // Also lookup locally when VIN reaches 17 valid chars, in case NHTSA rejects a valid-format VIN
  const onVinChange = (v) => {
    setVin(v);
    if (v.length !== 17) setResult(null);
    else if (/^[A-HJ-NPR-Z0-9]{17}$/.test(v)) lookup(v);
  };

  const updateStatus = async (job, newStatus) => {
    setStatusUpdating(job.id);
    try {
      await api.patch(`/jobs/${job.id}/status`, { status: newStatus });
      toast.success(`Status atualizado: ${STATUS_LABELS[newStatus] || newStatus}`);
      await lookup(vin);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar status");
    } finally { setStatusUpdating(null); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="check-status-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Consulta rápida</div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <ScanLine className="w-6 h-6 text-primary" /> Check Status
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Escaneie o VIN do parabrisa ou etiqueta da porta para ver todos os serviços ativos e trocar o status na hora.
        </p>
      </div>

      <div className="border border-border rounded-md bg-card p-4">
        <VinScanner
          value={vin}
          onChange={onVinChange}
          onDecoded={onDecoded}
          showResult={true}
        />
      </div>

      {loading && <div className="text-sm text-muted-foreground">Buscando…</div>}

      {result && result.found === false && (
        <div className="border border-dashed border-border rounded-md p-8 text-center" data-testid="vin-not-found">
          <AlertTriangle className="w-8 h-8 mx-auto text-orange-500 mb-2" />
          <div className="font-display font-bold">Veículo não cadastrado</div>
          <p className="text-sm text-muted-foreground mb-4">Este VIN <span className="font-mono">{result.vin}</span> não está no sistema.</p>
          <Button onClick={() => navigate(`/check-in?vin=${result.vin}`)} data-testid="checkin-from-vin-btn">
            <PlusCircle className="w-4 h-4" /> Iniciar Check-In com este VIN
          </Button>
        </div>
      )}

      {result && result.found && (
        <div className="space-y-4" data-testid="vin-found">
          <div className="border border-border rounded-md bg-card overflow-hidden">
            <div className="h-40 bg-secondary">
              {result.vehicle.photo_url ?
                <img src={result.vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center"><Car className="w-10 h-10 text-muted-foreground" /></div>}
            </div>
            <div className="p-5 flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Veículo</div>
                <div className="font-display text-2xl font-bold tracking-tight">
                  {result.vehicle.year} {result.vehicle.make} {result.vehicle.model}
                </div>
                <div className="text-sm text-muted-foreground font-mono">
                  {result.vehicle.plate || "sem placa"} · VIN {result.vehicle.vin}
                </div>
                {result.client && (
                  <button onClick={() => navigate(`/clientes/${result.client.id}`)} className="mt-1 text-sm text-primary hover:underline inline-flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />{result.client.name}
                  </button>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={() => lookup(vin)} data-testid="refresh-status-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Atualizar
              </Button>
            </div>
          </div>

          {result.active_jobs.length === 0 ? (
            <div className="border border-dashed border-border rounded-md p-8 text-center">
              <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
              <div className="font-display font-bold">Nenhum serviço ativo</div>
              <p className="text-sm text-muted-foreground mb-4">Este veículo não tem Jobs em aberto no momento.</p>
              <Button onClick={() => navigate(`/check-in?vin=${result.vin}`)}>
                <PlusCircle className="w-4 h-4" /> Novo Check-In
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Serviços ativos ({result.active_jobs.length})</div>
              {result.active_jobs.map((j) => {
                const meta = JOB_TYPE_META[j.job_type] || JOB_TYPE_META.mechanic;
                const statuses = STATUSES_BY_TYPE[j.job_type] || STATUSES_BY_TYPE.mechanic;
                const insp = j.inspection;
                const urg = insp?.items?.filter((x) => x.status === "urgente").length || 0;
                const att = insp?.items?.filter((x) => x.status === "atencao").length || 0;
                return (
                  <div key={j.id} className="border border-border rounded-md bg-card p-4" data-testid={`active-job-${j.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <meta.icon className={`w-3.5 h-3.5 ${meta.cls}`} />
                          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{meta.label}</span>
                          <span className="text-[10px] font-mono text-muted-foreground">· {j.days_in_shop}d</span>
                        </div>
                        <div className="font-display font-semibold">{j.service_type}</div>
                        <div className="text-xs text-muted-foreground">Técnico: {j.technician || "—"} · Prioridade: {j.priority}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Status atual</div>
                        <span className="px-2 py-1 rounded bg-primary/15 text-primary text-xs font-medium">
                          {STATUS_LABELS[j.status] || j.status}
                        </span>
                      </div>
                    </div>

                    {/* Quick status update */}
                    <div className="mt-3 border-t border-border pt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground md:col-span-1">
                        Mudar status para:
                      </div>
                      <div className="md:col-span-2 flex gap-2">
                        <Select
                          value={j.status}
                          onValueChange={(v) => updateStatus(j, v)}
                          disabled={statusUpdating === j.id}
                        >
                          <SelectTrigger className="h-9" data-testid={`status-select-${j.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {statuses.map((s) => (
                              <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Quick facts */}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {insp && (
                        <button
                          onClick={() => navigate(`/inspecoes/${insp.id}`)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary hover:bg-accent text-xs"
                          data-testid={`insp-link-${insp.id}`}
                        >
                          <ClipboardList className="w-3 h-3" /> Inspeção
                          {urg > 0 && <span className="text-red-500 font-mono">{urg}!</span>}
                          {att > 0 && <span className="text-orange-500 font-mono">{att}</span>}
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/estimate/${j.id}`)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary hover:bg-accent text-xs"
                        data-testid={`est-link-${j.id}`}
                      >
                        <DollarSign className="w-3 h-3" /> Orçamento
                        {j.estimate?.total > 0 && <span className="font-mono">${j.estimate.total.toFixed(2)}</span>}
                        <ChevronRight className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => navigate(`/${j.job_type === "body_shop" ? "body-shop" : j.job_type === "warranty" ? "garantias" : "kanban"}`)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-secondary hover:bg-accent text-xs"
                      >
                        <meta.icon className="w-3 h-3" /> Ver no Kanban <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {result.history.length > result.active_jobs.length && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Histórico completo</div>
              <div className="border border-border rounded-md divide-y divide-border">
                {result.history.map((h) => (
                  <div key={h.id} className="px-4 py-2 flex items-center gap-3 text-sm">
                    <FileDown className="w-3.5 h-3.5 text-muted-foreground" />
                    <div className="flex-1 truncate">{h.service_type}</div>
                    <div className="text-xs text-muted-foreground font-mono">{new Date(h.created_at).toLocaleDateString("pt-BR")}</div>
                    <div className="text-xs px-2 py-0.5 rounded bg-secondary">{STATUS_LABELS[h.status] || h.status}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
